import {
  AGENT_ENDPOINT_PAGE_SIZE,
  exportCSVFile,
  getAlphaComparator,
  UTC_DATETIME_FORMAT,
} from '../../../constants';
import { Button, Table } from 'reactstrap';
import Icon from '../../../widgets/Icons';
import * as React from 'react';
import { AgentRow, IPObj } from '../../../types';
import { useSelector } from 'react-redux';
import { sessionSelector } from '../../../reducers/session';
import moment from 'moment';
import StatusRenderer from '../../../widgets/StatusRenderer';
import LightTooltip, {
  TOOLTIP_DELAY_INSTANT_SHOW_INSTANT_HIDE,
} from '../../../widgets/LightTooltip';
import SortableTableColumnHeader from '../../../widgets/SortableTableColumnHeader';
import { memoizedAgentSelectors } from '../../../selectors';
import { useDeactivateAgent } from './useDeactivateAgent';
import { AgentColumnAction } from './AgentColumnAction';
import { withTrashableContext } from '../../../hooks';
import { Typography } from '@rtkwlf/ui';
import styled from 'styled-components';

export const CSV_HEADERS: Map<keyof AgentRow, string> = new Map();
// preserve order
CSV_HEADERS.set('hostname', 'Endpoint Name');
CSV_HEADERS.set('userName', 'Last User');
CSV_HEADERS.set('ipAddress', 'IP Address');
CSV_HEADERS.set('status', 'Status');
CSV_HEADERS.set('OSVersion', 'Operating System');
CSV_HEADERS.set('architecture', 'Operating System (Architecture)');
CSV_HEADERS.set('heartbeatTime', 'Last Reported');
CSV_HEADERS.set('contained', 'Containment');
CSV_HEADERS.set('softwareVersion', 'Agent Version');
CSV_HEADERS.set('id', 'Client ID');
CSV_HEADERS.set('deploymentID', 'Deployment ID');
CSV_HEADERS.set('macAddress', 'MAC Address');

type ExportButtonProps = {
  disabled?: boolean;
  tooltip?: string;
};

export const AgentExportButton: React.FunctionComponent<ExportButtonProps> = (
  props,
) => {
  const { tooltip, disabled = false } = props;
  const agentList = useSelector(memoizedAgentSelectors.selectCompleteAgentRows);
  const customerName = useSelector(sessionSelector.getCustomerName);

  const onExport = React.useCallback(() => {
    if (agentList.length > 0) {
      const filename = `${
        customerName || 'customer'
      }-agent-endpoints-${moment().format(UTC_DATETIME_FORMAT)}.csv`;
      exportCSVFile(CSV_HEADERS, agentList, filename);
    }
  }, [agentList, customerName]);

  let button = (
    <Button
      color="primary"
      outline
      disabled={agentList.length === 0 || disabled}
      onClick={onExport}
    >
      <Icon.Export className="mr-2" />
      Export
    </Button>
  );

  if (tooltip) {
    button = (
      <LightTooltip tooltip={tooltip} placement="top">
        {button}
      </LightTooltip>
    );
  }

  return button;
};

export type AgentEndpointPagerProps = {
  index: number;
  total: number;
  onIndexChange: (event: React.SyntheticEvent<HTMLButtonElement>) => void;
  isLoading: boolean;
};

// a pager with only buttons and no input
export const AgentEndpointPager = (props: AgentEndpointPagerProps) => {
  const { index, total, onIndexChange, isLoading } = props;

  const currentStart = index + 1;
  const currentEnd = Math.min(
    total,
    currentStart + AGENT_ENDPOINT_PAGE_SIZE - 1,
  );

  const prevDisabled = currentStart === 1 || isLoading;
  const nextDisabled = currentEnd === total || isLoading;

  const lastPageIndex =
    (Math.ceil(total / AGENT_ENDPOINT_PAGE_SIZE) - 1) *
    AGENT_ENDPOINT_PAGE_SIZE;

  return (
    <div className="agent-page-selector">
      <Button
        color="link"
        onClick={onIndexChange}
        index={0}
        disabled={prevDisabled}
        className={'pl-0'}
        data-cy="go-to-start"
      >
        <Icon.AngleDoubleLeft />
      </Button>
      <Button
        color="link"
        onClick={onIndexChange}
        index={index - AGENT_ENDPOINT_PAGE_SIZE}
        disabled={prevDisabled}
        className="pl-0"
        data-cy="go-to-prev"
      >
        <Icon.AngleLeft />
      </Button>
      <span>
        {isLoading
          ? 'Loading endpoints...'
          : total === 0
          ? 'Showing 0 endpoints'
          : `Showing ${currentStart} - ${currentEnd} of ${total} endpoints`}
      </span>
      <Button
        color="link"
        onClick={onIndexChange}
        index={currentEnd}
        disabled={nextDisabled}
        className="pr-0"
        data-cy="go-to-next"
      >
        <Icon.AngleRight />
      </Button>
      <Button
        color="link"
        onClick={onIndexChange}
        index={lastPageIndex}
        disabled={nextDisabled}
        data-cy="go-to-end"
      >
        <Icon.AngleDoubleRight />
      </Button>
    </div>
  );
};

const AGENT_ENDPOINT_TOOLTIPS = {
  STATUS: {
    ONLINE: 'Last reported within 72 hours.',
    OFFLINE: 'Last reported 72 hours ago or longer.',
  },
  CONTAINMENT: {
    TRUE: 'Network connection is restricted to agent-use only.',
    FALSE: 'Network connection is not restricted.',
  },
};

const withInstantTooltip = (message: string) => (children: React.ReactNode) => {
  return (
    <LightTooltip
      delay={TOOLTIP_DELAY_INSTANT_SHOW_INSTANT_HIDE}
      tooltip={message}
      placement="top"
    >
      <span className="pt-2">{children}</span>
    </LightTooltip>
  );
};

const onlineStatus = withInstantTooltip(AGENT_ENDPOINT_TOOLTIPS.STATUS.ONLINE)(
  <StatusRenderer status="active" label="Online" />,
);

const offlineStatus = withInstantTooltip(
  AGENT_ENDPOINT_TOOLTIPS.STATUS.OFFLINE,
)(<StatusRenderer status="N/A" label="Offline" />);

const containedIcon = <Icon.BoxWithShadow size={14} className="mr-2" />;
const containedStatus = withInstantTooltip(
  AGENT_ENDPOINT_TOOLTIPS.CONTAINMENT.TRUE,
)(
  <StatusRenderer
    className="badge-contained"
    status="PACKED"
    label="Contained"
    icon={containedIcon}
  />,
);
const notContainedStatus = withInstantTooltip(
  AGENT_ENDPOINT_TOOLTIPS.CONTAINMENT.FALSE,
)(
  <StatusRenderer
    status="N/A"
    className="badge-not-contained"
    label="Not Contained"
  />,
);

const StyledHeading = styled(Typography.Title)`
  color: white;
`;

export const IPAddress = ({
  ipObj,
  textFilterValue,
}: {
  ipObj: IPObj[];
  textFilterValue: string;
}) => {
  const tooltipElm = (
    <div>
      {ipObj.map(({ name, ipAddresses }, index) => {
        const pStyle = index === ipObj.length - 1 ? { margin: 0 } : {};
        return (
          <React.Fragment key={name}>
            <StyledHeading level={5}>{name}</StyledHeading>
            <p style={pStyle}>{ipAddresses}</p>
          </React.Fragment>
        );
      })}
    </div>
  );

  const ips = ipObj
    .map((ip) => ip.ipAddresses)
    .join(',')
    .split(',');
  const ipList: string[] = [];

  ips.forEach((ip) => {
    if (textFilterValue && ip.includes(textFilterValue.toLocaleLowerCase())) {
      ipList.unshift(ip);
    } else {
      ipList.push(ip);
    }
  });

  const suffix = ipList.length > 3 ? '...' : '';
  const ipText = ipList.splice(0, 3).join(', ') + suffix;

  return (
    <LightTooltip
      delay={TOOLTIP_DELAY_INSTANT_SHOW_INSTANT_HIDE}
      tooltip={tooltipElm}
      placement="top"
    >
      <span className="pt-2">{ipText}</span>
    </LightTooltip>
  );
};

export const useTableSort = (agents: AgentRow[], initSort: string) => {
  const [sort, setSort] = React.useState(initSort);

  const toggleSort = React.useCallback(
    (column: string) => {
      if (sort === `-${column}`) {
        setSort(column);
      } else {
        setSort(`-${column}`);
      }
    },
    [sort, setSort],
  );

  const sortedAgents = React.useMemo(() => {
    if (sort.startsWith('-')) {
      return (
        agents
          .slice()
          // @ts-ignore yes there are some non-string fields but they'll never be used here
          .sort(getAlphaComparator(sort.substring(1)))
          .reverse()
      );
    }
    // @ts-ignore same as above
    return agents.slice().sort(getAlphaComparator(sort));
  }, [agents, sort]);

  return {
    headerProps: {
      sort,
      toggleSort,
    },
    sortedAgents,
  };
};

export type HeaderProps = {
  sort: string;
  toggleSort: (sort: string) => void;
};

type AgentEndpointTableProps = {
  total: number;
  agents: AgentRow[];
  headerProps: HeaderProps;
  textFilterValue: string;
};

export enum AGENT_TABLE_COL_KEY {
  ENDPOINT_NAME = 'hostname',
  LAST_USER = 'userName',
  IP_ADDRESS = 'ipAddress',
  STATUS = 'status',
  OPERATING_SYSTEM = 'OSVersion',
  LAST_REPORTED = 'heartbeatTime',
  CONTAINMENT = 'contained',
  ACTIONS = 'actions',
}

export const RawAgentEndpointTable = (props: AgentEndpointTableProps) => {
  const { total, agents, headerProps, textFilterValue } = props;
  const { modal, onConfirmDeactivate } = useDeactivateAgent();
  if (total === 0 || agents.length === 0) {
    return (
      <div className="text-center mt-5">
        {total === 0
          ? 'No endpoints found.'
          : 'No endpoints match the current filter criteria.'}
      </div>
    );
  }

  return (
    <>
      <Table className="agent-status-table">
        <thead>
          <tr>
            <SortableTableColumnHeader
              name={AGENT_TABLE_COL_KEY.ENDPOINT_NAME}
              {...headerProps}
            >
              Endpoint Name
            </SortableTableColumnHeader>
            <SortableTableColumnHeader
              name={AGENT_TABLE_COL_KEY.LAST_USER}
              {...headerProps}
            >
              Last User
            </SortableTableColumnHeader>
            <SortableTableColumnHeader
              name={AGENT_TABLE_COL_KEY.IP_ADDRESS}
              {...headerProps}
            >
              IP Address
            </SortableTableColumnHeader>
            <SortableTableColumnHeader
              name={AGENT_TABLE_COL_KEY.STATUS}
              {...headerProps}
            >
              Status
            </SortableTableColumnHeader>
            <SortableTableColumnHeader
              name={AGENT_TABLE_COL_KEY.OPERATING_SYSTEM}
              {...headerProps}
            >
              Operating System
            </SortableTableColumnHeader>
            <SortableTableColumnHeader
              name={AGENT_TABLE_COL_KEY.LAST_REPORTED}
              {...headerProps}
            >
              Last Reported
            </SortableTableColumnHeader>
            <SortableTableColumnHeader
              name={AGENT_TABLE_COL_KEY.CONTAINMENT}
              {...headerProps}
            >
              Containment
            </SortableTableColumnHeader>
            <SortableTableColumnHeader name={AGENT_TABLE_COL_KEY.ACTIONS}>
              Actions
            </SortableTableColumnHeader>
          </tr>
        </thead>
        <tbody>
          {agents.map((agent, index) => (
            <tr key={index}>
              <td>{agent[AGENT_TABLE_COL_KEY.ENDPOINT_NAME]}</td>
              <td>{agent[AGENT_TABLE_COL_KEY.LAST_USER]}</td>
              <td>
                <IPAddress
                  ipObj={agent['ipObj']}
                  textFilterValue={textFilterValue}
                />
              </td>
              <td>
                {agent[AGENT_TABLE_COL_KEY.STATUS] === 'Online'
                  ? onlineStatus
                  : offlineStatus}
              </td>
              <td>{agent[AGENT_TABLE_COL_KEY.OPERATING_SYSTEM]}</td>
              <td>
                {(agent[AGENT_TABLE_COL_KEY.LAST_REPORTED] ?? '')
                  .split('T')
                  .join('\n')}
              </td>
              <td>
                {agent[AGENT_TABLE_COL_KEY.CONTAINMENT] === 'Contained'
                  ? containedStatus
                  : notContainedStatus}
              </td>
              <td>
                <AgentColumnAction
                  agent={agent}
                  onDelete={onConfirmDeactivate}
                ></AgentColumnAction>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      {modal}
    </>
  );
};

export const AgentEndpointTable = withTrashableContext(RawAgentEndpointTable);
